import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import shared from './shared.module.css';
import styles from './SDKPage.module.css';

const EXAMPLE_TYPESCRIPT = `import { RivellumClient } from '@rivellum/sdk';

// Initialize client
const client = new RivellumClient({
  network: 'testnet',
  apiUrl: 'https://rpc.rivellum.network',
});

// Create a lobby
const lobby = await client.casual.lobby.create({
  capacity: 10,
  minPlayers: 2,
  mode: 'deathmatch',
  region: 'us-west',
});

// Join lobby
await client.casual.lobby.join({
  lobbyId: lobby.id,
  playerId: 'player-123',
});

// Get leaderboard
const leaderboard = await client.casual.leaderboard.get({
  mode: 'deathmatch',
  limit: 100,
});
`;

const EXAMPLE_UNITY = `using Rivellum.SDK;
using UnityEngine;

public class GameManager : MonoBehaviour
{
    private RivellumClient client;

    async void Start()
    {
        // Initialize client
        client = new RivellumClient(new ClientConfig
        {
            Network = "devnet",
            ApiUrl = "https://rpc.rivellum.network"
        });

        // Create lobby
        var lobby = await client.Casual.Lobby.CreateAsync(new LobbyCreateParams
        {
            Capacity = 10,
            MinPlayers = 2,
            Mode = "deathmatch",
            Region = "us-west"
        });

        Debug.Log($"Created lobby: {lobby.Id}");

        // Join lobby
        await client.Casual.Lobby.JoinAsync(new LobbyJoinParams
        {
            LobbyId = lobby.Id,
            PlayerId = "player-123"
        });
    }
}
`;

type Language = 'typescript' | 'unity';

export function SDKPage() {
  const [selectedLanguage, setSelectedLanguage] = useState<Language>('typescript');
  const [copiedSection, setCopiedSection] = useState<string | null>(null);

  const handleCopy = (text: string, section: string) => {
    navigator.clipboard.writeText(text);
    setCopiedSection(section);
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const currentExample = selectedLanguage === 'typescript' ? EXAMPLE_TYPESCRIPT : EXAMPLE_UNITY;

  return (
    <div className={shared.container}>
      {/* Header */}
      <div className={shared.header}>
        <h1 className={shared.title}>SDK Documentation</h1>
        <p className={shared.subtitle}>
          Learn how to use Rivellum SDKs to integrate protocol services into your application
        </p>
      </div>

      <div className={styles.sdkLayout}>
        {/* Sidebar */}
        <div className={styles.sidebar}>
          <div className={styles.sidebarCard}>
            <h2 className={styles.sidebarTitle}>Languages</h2>
            <div className={styles.languageButtons}>
              <button
                onClick={() => setSelectedLanguage('typescript')}
                className={selectedLanguage === 'typescript' ? styles.languageButtonActive : styles.languageButton}
              >
                TypeScript / JavaScript
              </button>
              <button
                onClick={() => setSelectedLanguage('unity')}
                className={selectedLanguage === 'unity' ? styles.languageButtonActive : styles.languageButton}
              >
                Unity C#
              </button>
            </div>

            <div className={styles.sidebarSection}>
              <h2 className={styles.sidebarTitle}>Sections</h2>
              <div className={styles.sidebarLinks}>
                <a href="#installation" className={styles.sidebarLink}>
                  Installation
                </a>
                <a href="#quickstart" className={styles.sidebarLink}>
                  Quick Start
                </a>
                <a href="#prefabs" className={styles.sidebarLink}>
                  Prefab APIs
                </a>
                <a href="#intents" className={styles.sidebarLink}>
                  Intents
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className={styles.mainContent}>
          {/* Installation */}
          <section id="installation" className={styles.section}>
            <h2 className={styles.sectionTitle}>
              Installation
            </h2>

            {selectedLanguage === 'typescript' ? (
              <>
                <p className={styles.sectionText}>
                  Install the Rivellum SDK using npm or yarn:
                </p>
                <div className={styles.codeBlock}>
                  <pre className={styles.code}>
                    <code>npm install @rivellum/sdk</code>
                  </pre>
                  <button
                    onClick={() => handleCopy('npm install @rivellum/sdk', 'install-ts')}
                    className={styles.copyButton}
                  >
                    {copiedSection === 'install-ts' ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className={styles.sectionText}>
                  Download the Unity package and import it into your project:
                </p>
                <button className={shared.buttonPrimary}>
                  Download Unity Package
                </button>
              </>
            )}
          </section>

          {/* Quick Start */}
          <section id="quickstart" className={styles.section}>
            <h2 className={styles.sectionTitle}>
              Quick Start
            </h2>

            <p className={styles.sectionText}>
              Get started with the {selectedLanguage === 'typescript' ? 'TypeScript' : 'Unity'} SDK:
            </p>

            <div className={styles.codeBlock}>
              <pre className={styles.code}>
                <code>{currentExample}</code>
              </pre>
              <button
                onClick={() => handleCopy(currentExample, 'quickstart')}
                className={styles.copyButton}
              >
                {copiedSection === 'quickstart' ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
          </section>

          {/* Prefab APIs */}
          <section id="prefabs" className={styles.section}>
            <h2 className={styles.sectionTitle}>
              Prefab APIs
            </h2>

            <p className={styles.sectionText}>
              The Rivellum SDK provides type-safe APIs for all prefab categories:
            </p>

            <div className={styles.apiList}>
              <div className={styles.apiCategory}>
                <h3 className={styles.apiCategoryTitle}>
                  Session & Competition
                </h3>
                <ul className={styles.apiMethods}>
                  <li><code>client.casual.lobby</code> - Lobby management</li>
                  <li><code>client.casual.leaderboard</code> - Leaderboard queries</li>
                  <li><code>client.casual.tournament</code> - Tournament brackets</li>
                  <li><code>client.casual.rewardVault</code> - Reward distribution</li>
                </ul>
              </div>

              <div className={styles.apiCategory}>
                <h3 className={styles.apiCategoryTitle}>
                  World & Zones
                </h3>
                <ul className={styles.apiMethods}>
                  <li><code>client.world.zone</code> - World zones</li>
                  <li><code>client.world.dungeon</code> - Dungeon rooms</li>
                  <li><code>client.world.spawn</code> - Spawn points</li>
                  <li><code>client.world.portal</code> - Portal management</li>
                </ul>
              </div>

              <div className={styles.apiCategory}>
                <h3 className={styles.apiCategoryTitle}>
                  Economy & Account
                </h3>
                <ul className={styles.apiMethods}>
                  <li><code>client.economy.token</code> - Fungible tokens</li>
                  <li><code>client.economy.collectible</code> - NFTs & collectibles</li>
                  <li><code>client.economy.vault</code> - Payment vaults</li>
                  <li><code>client.economy.marketplace</code> - Marketplace listings</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Intents */}
          <section id="intents" className={styles.section}>
            <h2 className={styles.sectionTitle}>
              Understanding Intents
            </h2>

            <p className={styles.sectionText}>
              Rivellum uses intent classes to optimize transaction costs:
            </p>

            <div className={styles.intentGrid}>
              <div className={styles.intentCardMicro}>
                <h4 className={styles.intentTitle}>MICRO</h4>
                <p className={styles.intentDescription}>
                  Very low cost, high throughput. Best for frequent actions.
                </p>
              </div>
              <div className={styles.intentCardMedium}>
                <h4 className={styles.intentTitle}>MEDIUM</h4>
                <p className={styles.intentDescription}>
                  Balanced cost and security. Default for most operations.
                </p>
              </div>
              <div className={styles.intentCardPremium}>
                <h4 className={styles.intentTitle}>PREMIUM</h4>
                <p className={styles.intentDescription}>
                  Maximum security. For high-value transactions.
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
